import React, { useState, useRef, useEffect } from 'react';
import { FaBell, FaTimes, FaCheck } from 'react-icons/fa';
import api from '../../../services/api';

const NotificationCenter = ({ unreadMessages, clients, vouchers = [], onNotificationClick, currentUser }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dismissedNotifications, setDismissedNotifications] = useState(new Set()); // Track dismissed notifications in this session
  const dropdownRef = useRef(null);

  // Charger les notifications au montage du composant
  useEffect(() => {
    fetchNotifications();
  }, []);

  // Recharger les notifications quand on ouvre le dropdown
  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen]);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      // Charger TOUTES les notifications (lues et non lues) pour pouvoir filtrer correctement
      const response = await api.get('/notifications');
      setNotifications(response.data.notifications || []);
    } catch (error) {
      console.error('Erreur lors du chargement des notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filtrer les messages: uniquement ceux des clients NON approuvés/rejetés
  const activeUnreadMessages = unreadMessages.filter(msg => {
    const client = clients.find(c => c.id === msg.client_id);
    return client && !['approved', 'rejected'].includes(client.status);
  });

  // Récupérer les IDs des notifications lues pour les exclure
  const readNotificationIds = notifications
    .filter(n => n.isRead || n.is_read)
    .map(n => n.referenceId || n.reference_id);

  // Créer une clé unique pour identifier une notification (type + referenceId)
  const getNotificationKey = (type, referenceId) => `${type}_${referenceId}`;

  // Détecter les nouveaux parrainages (< 24h et non lus)
  const newClients = clients.filter(client => {
    const now = new Date();
    const createdAt = new Date(client.created_at);
    const diffHours = (now - createdAt) / (1000 * 60 * 60);
    const notificationKey = getNotificationKey('new_client', client.id);
    return diffHours < 24 &&
      !['approved', 'rejected'].includes(client.status) &&
      !readNotificationIds.includes(client.id) &&
      !dismissedNotifications.has(notificationKey);
  });

  // Détecter les nouveaux bons d'achat (< 24h, non utilisés et non lus)
  const newVouchers = vouchers.filter(voucher => {
    const now = new Date();
    const createdAt = new Date(voucher.createdAt || voucher.created_at);
    const diffHours = (now - createdAt) / (1000 * 60 * 60);
    const notificationKey = getNotificationKey('new_voucher', voucher.id);
    return diffHours < 24 &&
      !voucher.isUsed &&
      !readNotificationIds.includes(voucher.id) &&
      !dismissedNotifications.has(notificationKey);
  });

  // Grouper les messages par client avec infos enrichies
  const groupedNotifications = activeUnreadMessages.reduce((acc, msg) => {
    const clientId = msg.client_id;
    const client = clients.find(c => c.id === clientId);

    if (!acc[clientId]) {
      acc[clientId] = {
        clientId,
        clientName: client ? `${client.first_name} ${client.last_name}` : 'Client inconnu',
        sponsorName: client?.sponsor_name || 'Parrain',
        messages: [],
        lastMessageTime: msg.created_at
      };
    }
    acc[clientId].messages.push(msg);
    // Garder le message le plus récent
    if (new Date(msg.created_at) > new Date(acc[clientId].lastMessageTime)) {
      acc[clientId].lastMessageTime = msg.created_at;
    }
    return acc;
  }, {});

  // Créer notifications pour les nouveaux parrainages
  const newClientNotifications = newClients.map(client => ({
    type: 'new_client',
    clientId: client.id,
    clientName: `${client.first_name} ${client.last_name}`,
    sponsorName: client.sponsor_name,
    lastMessageTime: client.created_at
  }));

  // Créer notifications pour les nouveaux bons d'achat
  const newVoucherNotifications = newVouchers.map(voucher => ({
    type: 'new_voucher',
    voucherId: voucher.id,
    clientName: `${voucher.firstName || voucher.first_name || 'Client'} ${voucher.lastName || voucher.last_name || ''}`.trim(),
    value: voucher.value,
    lastMessageTime: voucher.createdAt || voucher.created_at
  }));

  // Combiner messages, nouveaux clients et nouveaux vouchers
  const allNotifications = [
    ...Object.values(groupedNotifications).map(n => ({ ...n, type: 'message' })),
    ...newClientNotifications,
    ...newVoucherNotifications
  ].sort((a, b) => new Date(b.lastMessageTime) - new Date(a.lastMessageTime));

  const unreadCount = allNotifications.length;

  // Fermer le dropdown si on clique à l'extérieur
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleNotificationClick = async (notification, e) => {
    // Si clic sur le bouton check, ne pas propager
    if (e?.target.closest('.dismiss-btn')) {
      return;
    }

    // Marquer comme lue en cliquant n'importe où sur la notification
    await markNotificationAsRead(notification);

    if (notification.type === 'new_client' || notification.type === 'new_voucher') {
      // Nouveau parrainage ou voucher : juste fermer le dropdown
      setIsOpen(false);
    } else {
      // Message : ouvrir la discussion
      onNotificationClick(notification.clientId);
      setIsOpen(false);
    }
  };

  const markNotificationAsRead = async (notification) => {
    try {
      // Créer une notification en BDD si elle n'existe pas encore
      const referenceId = notification.clientId || notification.voucherId;
      const type = notification.type;

      // Ajouter immédiatement à la liste des notifications rejetées pour masquer de l'UI
      const notificationKey = getNotificationKey(type, referenceId);
      setDismissedNotifications(prev => new Set([...prev, notificationKey]));

      // Chercher la notification existante
      const existingNotif = notifications.find(
        n => (n.referenceId || n.reference_id) === referenceId && n.type === type
      );

      if (existingNotif) {
        // Marquer comme lue
        await api.post(`/notifications/${existingNotif.id}/read`);
      } else {
        // Créer puis marquer comme lue
        const response = await api.post('/notifications/create', {
          userId: currentUser.id,
          type: type,
          referenceId: referenceId,
          title: notification.type === 'new_client' ? 'Nouveau parrainage' :
            notification.type === 'new_voucher' ? 'Nouveau bon d\'achat' : 'Message',
          content: notification.clientName || notification.sponsorName || ''
        });

        if (response.data.notification) {
          await api.post(`/notifications/${response.data.notification.id}/read`);
        }
      }

      // Recharger les notifications
      await fetchNotifications();
    } catch (error) {
      console.error('Erreur lors du marquage de la notification:', error);
      // En cas d'erreur, retirer de la liste des rejetées pour permettre de réessayer
      const referenceId = notification.clientId || notification.voucherId;
      const type = notification.type;
      const notificationKey = getNotificationKey(type, referenceId);
      setDismissedNotifications(prev => {
        const newSet = new Set(prev);
        newSet.delete(notificationKey);
        return newSet;
      });
    }
  };

  const handleDismissNotification = async (id, type, e) => {
    e.stopPropagation();

    // Marquer comme lue
    const notification = {
      type,
      clientId: type === 'new_client' ? id : null,
      voucherId: type === 'new_voucher' ? id : null
    };
    await markNotificationAsRead(notification);
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'À l\'instant';
    if (diffMins < 60) return `Il y a ${diffMins} min`;
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    if (diffDays === 1) return 'Hier';
    return `Il y a ${diffDays}j`;
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bouton Bell */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2.5 hover:bg-white/10 rounded-lg transition-colors"
        style={{ color: 'inherit' }}
        title="Notifications"
      >
        <FaBell className="w-4 h-4" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[1.25rem] h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center px-1.5">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown Notifications */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl border border-gray-200 overflow-hidden z-50 shadow-xl">
          {/* Header */}
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900">
              Notifications {unreadCount > 0 && `(${unreadCount})`}
            </h3>
            <button
              onClick={() => setIsOpen(false)}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <FaTimes className="w-4 h-4" />
            </button>
          </div>

          {/* Liste des notifications */}
          <div className="max-h-96 overflow-y-auto">
            {allNotifications.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <p className="text-sm text-gray-500">Aucune nouvelle notification</p>
              </div>
            ) : (
              allNotifications.map((notif, index) => (
                <div
                  key={notif.clientId || notif.voucherId || `notif-${index}`}
                  onClick={(e) => handleNotificationClick(notif, e)}
                  className="w-full px-4 py-3 hover:bg-blue-50 transition-colors cursor-pointer border-b border-gray-100 last:border-b-0"
                >
                  <div className="flex items-start gap-3">
                    {/* Badge bleu */}
                    <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>

                    {/* Contenu */}
                    <div className="flex-1 min-w-0">
                      {notif.type === 'new_client' ? (
                        <>
                          <p className="text-sm font-medium text-gray-900 mb-1">
                            Nouvelle recommandation
                          </p>
                          <p className="text-xs text-gray-600 truncate">
                            {notif.clientName} parrainé par {notif.sponsorName}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            {formatTimeAgo(notif.lastMessageTime)}
                          </p>
                        </>
                      ) : notif.type === 'new_voucher' ? (
                        <>
                          <p className="text-sm font-medium text-gray-900 mb-1">
                            Nouveau bon d'achat
                          </p>
                          <p className="text-xs text-gray-600 truncate">
                            {notif.clientName} - {notif.value}€
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            {formatTimeAgo(notif.lastMessageTime)}
                          </p>
                        </>
                      ) : (
                        <>
                          <p className="text-sm font-medium text-gray-900 mb-1">
                            Nouveau message de <span className="font-semibold">{notif.sponsorName}</span>
                          </p>
                          <p className="text-xs text-gray-600 truncate">
                            Concernant le dossier de {notif.clientName}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            {formatTimeAgo(notif.lastMessageTime)} · {notif.messages?.length} message{notif.messages?.length > 1 ? 's' : ''}
                          </p>
                        </>
                      )}
                    </div>

                    {/* Bouton Check pour nouveaux parrainages et vouchers */}
                    {(notif.type === 'new_client' || notif.type === 'new_voucher') && (
                      <button
                        onClick={(e) => handleDismissNotification(
                          notif.type === 'new_client' ? notif.clientId : notif.voucherId,
                          notif.type,
                          e
                        )}
                        className="dismiss-btn p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors flex-shrink-0"
                        title="Marquer comme vu"
                      >
                        <FaCheck className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationCenter;

